export const AI_VIDEO_CONFIG = {
	credits: {
		type: "IMAGE_GENERATION" as const,
	},
} as const;

/** 全集。每个模型实际能选哪些看 VIDEO_MODELS */
export const VIDEO_RESOLUTIONS = ["480p", "720p", "1080p"] as const;

export type VideoResolution = (typeof VIDEO_RESOLUTIONS)[number];

/** `auto` = 跟随输入图的比例 */
export const VIDEO_ASPECT_RATIOS = [
	"auto",
	"1:1",
	"16:9",
	"9:16",
	"3:2",
	"2:3",
] as const;

export type VideoAspectRatio = (typeof VIDEO_ASPECT_RATIOS)[number];

type VideoModelConfig = {
	label: string;
	/** 上游模型标识，也是历史记录里 `model` 字段存的值 */
	providerModel: string;
	resolutions: readonly VideoResolution[];
	aspectRatios: readonly VideoAspectRatio[];
	/** 单次生成能传几张输入图 */
	maxImages: number;
	duration: { min: number; max: number; default: number };
	creditPerSec: Partial<Record<VideoResolution, number>>;
	/** 下架：不进下拉，但条目要留着，历史记录靠它显示模型名 */
	hidden?: boolean;
};

/**
 * 与 packages/api/src/routes/ai-video/lib/config.ts 手工保持一致，改价要两边一起改。
 * 实际扣费以服务端为准。顺序即下拉顺序。
 */
export const VIDEO_MODELS = {
	"grok-1.5": {
		label: "Grok 1.5",
		providerModel: "grok-imagine-video-1-5-preview",
		resolutions: ["480p", "720p", "1080p"],
		aspectRatios: ["auto", "1:1", "16:9", "9:16", "3:2", "2:3"],
		maxImages: 7,
		duration: { min: 1, max: 15, default: 5 },
		// 2026-09-14 与 Kie 积分 1:1，和后端 ai-video/lib/config.ts 保持一致。
		creditPerSec: { "480p": 2.4, "720p": 4.5, "1080p": 8 },
	},
	// fal Grok Lite。只做 kie 锁站时的服务端兜底和 10/5–10/6 历史行的 label，不进下拉
	"grok-1.5-fal": {
		label: "Grok 1.5",
		providerModel: "xai/grok-imagine-video/v1.5/lite/image-to-video",
		resolutions: ["480p", "720p", "1080p"],
		aspectRatios: ["auto"],
		maxImages: 1,
		duration: { min: 1, max: 15, default: 5 },
		creditPerSec: { "480p": 4, "720p": 6, "1080p": 28 },
		hidden: true,
	},
	"grok-1.5-fast": {
		label: "Grok 1.5 fast",
		providerModel: "xai/grok-imagine-video/v1.5/image-to-video",
		resolutions: ["480p", "720p", "1080p"],
		aspectRatios: ["auto"],
		maxImages: 1,
		duration: { min: 1, max: 15, default: 5 },
		creditPerSec: { "480p": 10, "720p": 17.5, "1080p": 30 },
		// 每秒单价是 grok-1.5 的 10 倍，对用户太贵，下架
		hidden: true,
	},
} as const satisfies Record<string, VideoModelConfig>;

export type VideoModelId = keyof typeof VIDEO_MODELS;

/** 全部模型，含已下架的。历史记录反查 label 用这个 */
export const VIDEO_MODEL_IDS = Object.keys(VIDEO_MODELS) as VideoModelId[];

/** 下拉里能选的模型 */
export const SELECTABLE_VIDEO_MODEL_IDS = VIDEO_MODEL_IDS.filter(
	(id) => !("hidden" in VIDEO_MODELS[id] && VIDEO_MODELS[id].hidden),
);

export const DEFAULT_VIDEO_MODEL: VideoModelId = "grok-1.5";

/** 历史记录存的是 providerModel，没匹配上就原样显示 */
export function getVideoModelLabel(providerModel: string): string {
	const id = VIDEO_MODEL_IDS.find(
		(key) => VIDEO_MODELS[key].providerModel === providerModel,
	);
	return id ? VIDEO_MODELS[id].label : providerModel;
}

export function getVideoModelIdByProviderModel(
	providerModel: string,
): VideoModelId | undefined {
	return VIDEO_MODEL_IDS.find(
		(key) => VIDEO_MODELS[key].providerModel === providerModel,
	);
}

export function getVideoCreditCost(
	model: VideoModelId,
	resolution: VideoResolution,
	duration: number,
): number {
	const perSec = (
		VIDEO_MODELS[model].creditPerSec as Partial<
			Record<VideoResolution, number>
		>
	)[resolution];
	return Math.round((perSec ?? 0) * duration);
}

/**
 * 生成耗时的预估区间。基于 2026-07-29 直接打 kie grok-1.5 的两轮实测（480p/720p ×
 * 1/5/10/15s，共 14 个任务）：耗时随时长和分辨率单调上升，但上游排队占大头——同一
 * 组参数两轮能差一倍（480p·5s 实测 26.6s 和 70.9s）。所以只给区间：低端贴空闲时的
 * 实测值，高端 1.8 倍覆盖排队高峰。
 * 历史记录里的 resolution 是裸字符串，没实测过的（1080p）按 720p 这档估。
 * 时长上限 15s 时高端也就 190s，一律用秒——换成分钟反而会因取整跳档（1–3 分钟）。
 */
export function getVideoEtaLabel(resolution: string, duration: number): string {
	const { base, perSec } =
		resolution === "480p"
			? { base: 25, perSec: 3 }
			: { base: 30, perSec: 5 };
	const low = Math.round((base + perSec * duration) / 5) * 5;
	const high = Math.round((low * 1.8) / 5) * 5;

	return `Usually takes ${low}–${high} seconds`;
}

export type VideoTaskStatus =
	| "GENERATING"
	| "PROCESSING"
	| "SUCCESS"
	| "FAILED";

export type { VideoHistoryItem } from "@saas/shared/history/types";

/** KIE Grok 1.5 的 1080p 仅支持单图。 */
export function getVideoMaxImages(
	model: VideoModelId,
	resolution: VideoResolution,
): number {
	return model === "grok-1.5" && resolution === "1080p"
		? 1
		: VIDEO_MODELS[model].maxImages;
}
